import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Wallet, Gift, ChevronRight } from 'lucide-react';

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * WalletStack — the official BETohalics wallet UI (Jun 2026).
 * Three stacked cards: Credits (main balance), Free Play balance (bonus),
 * and Add Credits (deposit). Replaces the old welcome banner + free-play
 * chip + buy-credits card. All values are live from /auth/me (authMe).
 */
const WalletStack = ({ name, credits = 0, freePlay = 0, dailyBonusReady = false, rollover = 0 }) => {
    const navigate = useNavigate();
    const total = Number(credits || 0) + Number(freePlay || 0);

    // Flash the balance number when it changes (win / spend feedback).
    const prev = useRef(total);
    const [flash, setFlash] = useState(null);
    useEffect(() => {
        if (prev.current === total) return;
        setFlash(total > prev.current ? 'up' : 'down');
        prev.current = total;
        const t = setTimeout(() => setFlash(null), 1300);
        return () => clearTimeout(t);
    }, [total]);

    return (
        <div className="space-y-4" data-testid="wallet-stack">
            {/* 1 — Credits (main wallet) */}
            <motion.div
                initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
                className="relative overflow-hidden rounded-2xl border border-[#FFD700]/70 bg-[#0a0a0a] p-5 sm:p-6"
                style={{ boxShadow: '0 0 30px rgba(255,215,0,0.10), inset 0 1px 0 rgba(255,255,255,0.05)' }}
                data-testid="wallet-card-credits"
            >
                <span className="pointer-events-none absolute -right-10 -top-16 h-56 w-56 rounded-full blur-3xl"
                    style={{ background: 'radial-gradient(circle, rgba(255,180,0,0.18), transparent 70%)' }} />
                <div className="relative flex items-center gap-4">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-[#FFD700]/60"
                        style={{ background: 'rgba(255,215,0,0.10)' }}>
                        <Wallet className="h-7 w-7 text-[#FFD700]" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-[10px] font-black uppercase tracking-[0.3em] text-[#FFD700]/80">Welcome back</p>
                        <h1 className="truncate text-3xl font-black text-white sm:text-4xl" data-testid="wallet-name">{name}</h1>
                    </div>
                </div>
                <div className="relative my-4 h-px bg-gradient-to-r from-[#FFD700]/50 to-transparent" />
                <p className="text-[10px] font-black uppercase tracking-[0.3em] text-[#FFD700]/80">Balance</p>
                <motion.p
                    key={`${total}-${flash || 'idle'}`}
                    animate={flash === 'up' ? { scale: [1, 1.06, 1] } : flash === 'down' ? { scale: [1, 0.97, 1] } : {}}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                    className="relative text-4xl font-black tabular-nums text-white sm:text-5xl"
                    data-testid="wallet-credits-amount"
                >
                    {fmt$(total)}
                </motion.p>
            </motion.div>

            {/* 2 — Free Play balance (bonus) */}
            <motion.div
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.05 }}
                className="relative overflow-hidden rounded-2xl border border-[#C77DFF]/50 p-5 sm:p-6"
                style={{ background: 'linear-gradient(135deg, #150a2a 0%, #0a0a0a 60%)', boxShadow: '0 0 30px rgba(199,125,255,0.10)' }}
                data-testid="wallet-card-freeplay"
            >
                <span className="pointer-events-none absolute -right-10 -bottom-16 h-56 w-56 rounded-full blur-3xl"
                    style={{ background: 'radial-gradient(circle, rgba(150,80,255,0.20), transparent 70%)' }} />
                <div className="relative flex items-center gap-4">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-[#C77DFF]/60"
                        style={{ background: 'rgba(150,80,255,0.14)' }}>
                        <Gift className="h-7 w-7 text-[#E0B24A]" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#E0B24A]">Included Free Play</p>
                        <p className="text-3xl font-black tabular-nums text-white sm:text-4xl" data-testid="wallet-freeplay-amount">{fmt$(freePlay)}</p>
                        <p className="text-[10px] font-semibold text-white/50">part of your balance above</p>
                    </div>
                    <button
                        onClick={() => navigate('/rewards')}
                        data-testid="wallet-earn-more-btn"
                        className={`relative inline-flex shrink-0 items-center gap-1 rounded-lg px-4 py-2.5 text-sm font-black text-black transition-transform hover:scale-[1.03] ${dailyBonusReady ? 'animate-pulse' : ''}`}
                        style={{ background: 'linear-gradient(135deg,#FFD700,#FF8C00)', boxShadow: '0 0 16px rgba(255,180,0,0.35)' }}
                    >
                        {dailyBonusReady && (
                            <span
                                className="absolute -right-1.5 -top-2 rounded-full bg-[#39FF14] px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wide text-black shadow-[0_0_10px_rgba(57,255,20,0.7)]"
                                data-testid="wallet-claim-ready-badge"
                            >
                                Claim ready
                            </span>
                        )}
                        EARN MORE <ChevronRight className="h-4 w-4" />
                    </button>
                </div>
                <div className="relative mt-3 h-px bg-gradient-to-r from-[#C77DFF]/40 to-transparent" />
                {rollover > 0 ? (
                    <p className="relative mt-2 text-xs font-semibold text-white/80" data-testid="wallet-rollover-hint">
                        Wager <b className="text-[#FFD700]">{fmt$(rollover)}</b> more to unlock for cash-out
                    </p>
                ) : (
                    <p className="relative mt-2 text-xs font-semibold text-white/70">
                        Bonuses <span className="mx-1 text-[#C77DFF]">·</span> Referrals <span className="mx-1 text-[#C77DFF]">·</span> Promotions
                    </p>
                )}
            </motion.div>

            {/* 3 — Add Credits (deposit) */}
            <motion.div
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}
                className="relative overflow-hidden rounded-2xl border border-[#FF8C00]/50 p-5 sm:p-6"
                style={{ background: 'linear-gradient(135deg, #1a1206 0%, #0a0a0a 60%)', boxShadow: '0 0 30px rgba(255,140,0,0.10)' }}
                data-testid="wallet-card-add-credits"
            >
                <span className="pointer-events-none absolute -left-10 -bottom-16 h-56 w-56 rounded-full blur-3xl"
                    style={{ background: 'radial-gradient(circle, rgba(255,140,0,0.20), transparent 70%)' }} />
                <div className="relative flex items-center gap-4">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-[#FF8C00]/60"
                        style={{ background: 'rgba(255,140,0,0.14)' }}>
                        <Wallet className="h-7 w-7 text-[#FFB300]" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="text-lg font-black uppercase tracking-[0.15em] text-white sm:text-xl">Add Credits</p>
                        <p className="mt-0.5 text-xs font-semibold text-white/60">Debit/Credit Card · PayPal · Venmo · Apple Pay</p>
                    </div>
                    <button
                        onClick={() => navigate('/buy-credits')}
                        data-testid="wallet-load-now-btn"
                        className="inline-flex shrink-0 items-center gap-1 rounded-lg px-5 py-2.5 text-sm font-black text-black transition-transform hover:scale-[1.03]"
                        style={{ background: 'linear-gradient(135deg,#FFD700,#FF8C00)', boxShadow: '0 0 16px rgba(255,180,0,0.4)' }}
                    >
                        LOAD NOW <ChevronRight className="h-4 w-4" />
                    </button>
                </div>
            </motion.div>
        </div>
    );
};

export default WalletStack;
